import './chooseImage.scss';
import { lang } from '@umu/ukit-lang';
import React from 'react';
import { isUmuWx } from '@umu/umu-ua';
import { components } from 'common';
const { If } = components;
import { UploadLoading } from '../../../@uploadLoading/uploadLoading';
import { LANG } from '../../../../@utils/webview';

import UmuWebview from '@umu/umu-webview';
import { $window } from '@umu/umu-dom';
import { getGuid } from '@umu/umu-util';
import { uploadImageToServer } from './imgUtilService';
import { PhotoBrowserUploader, IEndData } from '../photoBrowserUploader/photoBrowserUploader';

export const MAX_PHOTO_COUNT = 9;

interface IProps {
    teacherId: string;
    children: JSX.Element | string;
    onChoose: (urls: IChooseImageInfo[]) => void;
    currentCount?: number;
    multiple?: boolean;
    onlyPreview?: boolean;
    noUseAppUpload?: boolean;
    onPreview?: (objectUrl: string, file: File) => void;
    onError?: (e: Error) => void;
    onBrowserSelectReady?: (files: Array<{ file: File; previewUrl: string }>) => void;
}

export interface IChooseImageInfo {
    id: string;
    url: string;
    desc: string;
    file_size?: number;
    resource_id?: string;
}

interface IPhoto {
    id: string;
    url: string;
    desc: string;
    resource_id?: string;
}

export function ChooseImage(props: IProps) {
    const { currentCount = 0, onlyPreview, noUseAppUpload, onError } = props;
    const onBrowserSelectReady = (
        files: {
            file: File;
            previewUrl: string;
        }[],
    ) => {
        onlyPreview && hideLoading();
        props.onBrowserSelectReady && props.onBrowserSelectReady(files);
    };

    // getPhoto   type : camera album  count : 0 不限 1 单选 其他 多选限制几张
    // receivePhoto   success 1 ， 旧 imgUrl, 新 imgArr [url ,,,,,]
    // 如果是 UMU APP 里打开则不使用微信
    const chooseImgInWebview = () => {
        UmuWebview.registerHandler('receivePhoto', (data, _callback) => {
            if (!data.success) {
                return;
            }
            if (data.imgUrl) {
                localFileUploadSuccessCallback([data.imgUrl]);
            } else if (data.imgArr) {
                const list = data.imgArr || [];
                localFileUploadSuccessCallback(list);
            }
        });

        UmuWebview.callHandler(
            'getPhoto',
            {
                type: ['camera', 'album'],
                isCrop: false,
                count: MAX_PHOTO_COUNT - currentCount,
            },
            () => void 0,
            LANG,
        );
    };

    const chooseImgInWx = () => {
        let localIds = [];
        let inFrame = false; // 组件是否在iframe中
        if (self !== top) {
            inFrame = true;
        }
        const topWindow = parent.parent || {};
        const wx = $window.wx || (inFrame && topWindow.wx);
        wx.chooseImage({
            count: MAX_PHOTO_COUNT - currentCount,
            sourceType: ['camera', 'album'],
            success(result: any) {
                if (result.errMsg === 'chooseImage:ok') {
                    localIds = result.localIds;
                    if (Array.isArray(localIds)) {
                        savePhotoListInWx(localIds).then((list) => {
                            localFileUploadSuccessCallback(list);
                        });
                    } else {
                        savePhotoListInWx([localIds]).then((list) => {
                            localFileUploadSuccessCallback(list);
                        });
                    }
                }
            },
            fail(e: any) {
                console.warn(e);
                onError && onError(e);
            },
        });
    };

    const choosePhotoInWebview = (e: React.MouseEvent) => {
        e.preventDefault();
        if (noUseAppUpload || !isUmuWx) {
            return;
        }
        if (UmuWebview && UmuWebview.UA_INFO.isUMUWebview) {
            chooseImgInWebview();
            return;
        }
        chooseImgInWx();
    };

    const showLoading = () => {
        UploadLoading.show({ message: lang('正在上传') });
    };

    const hideLoading = () => {
        UploadLoading.hide();
    };

    const localFileSelectedCallback = () => {
        showLoading();
    };

    const localFileUploadSuccessCallback = (urlList: string[]) => {
        const photoList: IPhoto[] = [];
        urlList.forEach((url: string) => {
            photoList.push({
                id: getGuid(),
                url,
                desc: '',
            });
        });
        props.onChoose(photoList);
    };

    // 微信中上传
    const savePhotoListInWx = (photoList: string[]): Promise<string[]> => {
        const list = photoList.concat([]);
        const completeList: string[] = [];

        return new Promise((resolve) => {
            const syncUpload = () => {
                const item = list.shift();
                if (!item) {
                    return;
                }
                wx.uploadImage({
                    localId: item,
                    isShowProgressTips: 1,
                    success(res) {
                        const mediaId = res.serverId;
                        const done = () => {
                            if (list.length === 0) {
                                resolve(completeList);
                            } else {
                                syncUpload();
                            }
                        };
                        uploadImageToServer({ media_id: mediaId })
                            .then((data) => {
                                completeList.push(data.data.photo_url);
                                done();
                            })
                            .catch(() => {
                                done();
                            });
                    },
                    fail(e) {
                        onError && onError(e);
                        console.warn(e.errMsg);
                    },
                });
            };
            syncUpload();
        });
    };

    const localFileUploadErrorCallback = (e: Error) => {
        onError && onError(e);
        hideLoading();
    };

    const onEnd = (datas: IEndData[]) => {
        const images = datas.map((item) => ({
            id: getGuid(),
            url: item.url,
            desc: '',
            resource_id: item.resourceId,
        }));
        props.onChoose(images);
        hideLoading();
    };

    // 浏览器环境下的按钮
    const renderBrowserUploadBtn = () => {
        return (
            <div className="browser-upload-btn">
                <PhotoBrowserUploader
                    multiple={!!props.multiple}
                    teacherId={props.teacherId}
                    disabled={false}
                    buttonClass={'file-btn'}
                    onPreview={props.onPreview}
                    onlyPreview={props.onlyPreview}
                    fileSelectedCallback={localFileSelectedCallback}
                    onUploadError={localFileUploadErrorCallback}
                    onEnd={onEnd}
                    onSelectReady={onBrowserSelectReady}
                >
                    {props.children}
                </PhotoBrowserUploader>
            </div>
        );
    };

    return (
        <div className="choose-photo-wrap" onMouseDown={choosePhotoInWebview}>
            <If condition={noUseAppUpload || !isUmuWx}>{renderBrowserUploadBtn()}</If>
            <If condition={!noUseAppUpload && isUmuWx}>
                <>{props.children}</>
            </If>
        </div>
    );
}
